<script lang="ts">
const MAX_AVATARS = 5
</script>
<script setup lang="ts">import { computed as _computed } from 'vue';

/**
 *
 * Displays a set of user and team avatars
 * Optional features to use facepile style (with icon only), truncate the list, and prioritize the current user
 *
 */
import { getModule } from 'vuex-module-decorators';
import type { UserDto } from '@/api';
import { NO_OWNER_ID } from '@/components/SAvatars/SAvatars';
import authModule from '@/store/modules/auth';
import store from '@/store';
import stringToInitials from '@/utils/filters/stringToInitials';
import initialsToColor from '@/utils/initialsToColor';
import i18n from '@/i18n';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';

type SAvatarsUser = UserDto & { type?: string };


const auth = getModule(authModule, store);

interface SAvatarsProps {
  /**
   * List of users and teams to display
   */
  users: SAvatarsUser[];
  /**
   * Whether to limit displayed users to MAX_AVATARS
   */
  truncate?: boolean;
  /**
   * Whether to use facepile style (right-to-left order)
   */
  facepile?: boolean;
  /**
   * Whether to reduce the spacing between avatars
   */
  tighten?: boolean;
  /**
   * Whether to wrap the avatars in container, with line breaks if needed
   */
  wrapped?: boolean;
  /**
   * Size of the avatars
   */
  size?: number | string;
  /**
   * Whether to prioritize current user
   */
  prioritizeCurrentUser?: boolean;
}

const props = defineProps({
  users: null,
  truncate: { type: Boolean, default: false },
  facepile: { type: Boolean, default: false },
  tighten: { type: Boolean, default: false },
  wrapped: { type: Boolean },
  size: { default: 22 },
  prioritizeCurrentUser: { type: Boolean, default: true }
});

// Whether the list is truncated
const isTruncated = _computed(() => props.truncate && props.users.length > MAX_AVATARS);

/**
 * Utility functions for user display
 */
const isTeam = (user: SAvatarsUser): boolean => user.type === 'team' || user.type === 'TEAM';

// List of users to be displayed, with optional reordering and truncation
const visibleUsers = _computed(() => {
  const tempUsers = [...props.users];

  // Optional: Move current user to the front (only for users, not teams)
  if (props.prioritizeCurrentUser) {
    const currentUserId = auth.getUser ? auth.getUser.id : null;
    const currentUserIndex = currentUserId != null ? tempUsers.findIndex((user) => !isTeam(user) && user.id === currentUserId) : -1;

    if (currentUserIndex !== -1) {
      const currentUser = tempUsers.splice(currentUserIndex, 1);
      tempUsers.unshift(currentUser[0]);
    }
  }
  const usersToDisplay = props.truncate ? tempUsers.slice(0, MAX_AVATARS) : tempUsers;
  return props.facepile ? usersToDisplay.reverse() : usersToDisplay;
});

// Text to display when users are truncated
const plusUsersText = _computed(() => `+${props.users.length - MAX_AVATARS}`);

const isNoOwnerId = (user: UserDto) => user.id === NO_OWNER_ID;

/**
 * Format name special case for current user
 * @param user
 */
const getUserName = (user: SAvatarsUser) => {
  if (!user || !user.name) return '';
  if (isTeam(user)) return user.name;
  const isCurrentUser = auth.getUser && (auth.getUser.id === user.id || auth.getUser.email === user.id);
  return isCurrentUser ? i18n.t('users.current_user_display', { user: user.name }) : user.name;
};

/**
 * Get user initials
 * @param user
 */
const getUserInitials = (user: UserDto) => (user?.name ? stringToInitials(user.name) : '');

const getTeamInitials = (team: SAvatarsUser): string => (team?.name ? stringToInitials(team.name) : '');

/**
 * Get user color based on initials
 * @param user
 */
const getUserColor = (user: UserDto) => (user?.name ? initialsToColor(user.name) : '');
</script>

<template lang="pug">
.s-avatars.d-flex.align-center(v-bind="$attrs")
  .avatars-container.d-flex.align-center(:class="{ facepile, wrapped }")
    .avatar-item.d-flex.align-center.text-truncate(
      v-for="(user, index) in visibleUsers"
      :key="user.id"
      :class="{'mr-4': !tighten && !facepile && index !== visibleUsers.length - 1, 'mr-2': tighten && index !== visibleUsers.length - 1, 'mb-1': wrapped }"
    )
      v-tooltip( top :disabled="!facepile" open-delay="200" max-width="500"  )
        template(v-slot:activator="{ on }")
          .d-flex.align-baseline.text-truncate( v-on="on" )
            v-avatar.mr-1.align-self-center(
              v-if="!isNoOwnerId(user) && !isTeam(user)"
              :size="size"
              :color="getUserColor(user)"
            )
              span.white--text(:style="{ fontSize: '8px' }") {{ getUserInitials(user) }}
            v-icon.mr-1( v-else-if="isNoOwnerId(user)" :size="size" color="iconNeutral" ) icon-user-circle-dashed
            v-avatar.team-avatar.mr-1( v-else-if="isTeam(user)" :size="size" color="iconNeutral" )
              span(:style="{ fontSize: '8px' }") {{ getTeamInitials(user) }}
            SEllipsisTooltip.body-2.text-truncate.align-self-center(v-if="!facepile" :content="getUserName(user)" data-track="user-name" data-pendo="ignore")
        .d-flex.flex-column.align-center(data-pendo="ignore")
          span(v-if="getUserName(user) !== user.login") {{ getUserName(user) }}
          span(v-if="!isTeam(user)") {{ user.login }}

  // Show count of hidden users if truncated
  span.avatars-truncated-count.caption.grey--text( v-if="isTruncated" data-track="truncated-users-sign" ) {{ plusUsersText }}
</template>

<style lang="scss" scoped>
.s-avatars {
  max-width: 100%;
  .avatars-container {
    max-width: 100%;
  }

  .v-avatar {
    border: 1px solid map-get($shades, 'white') !important;
  }

  .facepile {
    display: flex;
    flex-direction: row-reverse;
    justify-content: flex-end;

    .avatar-item {
      margin-left: -8px; // Overlap avatars slightly

      &:last-child {
        margin-left: 0;
      }
    }
  }

  .wrapped {
    flex-wrap: wrap;
  }

  .avatars-truncated-count {
    margin-top: 2px;
  }

  .team-avatar {
    border: 1px solid var(--v-borderInverse-base) !important;
    background-color: var(--v-bgPrimary-base) !important;
    color: var(--v-textPrimary-base) !important;
  }
}
</style>
